'use client';

import isEqual from 'lodash-es/isEqual';
import { type ReactElement, type ReactNode, useState } from 'react';
import { useCallback, useMemo } from 'react';

import type { Entity } from '@/api/types/entityTypes';

import { EntityContext, type EntityContextType } from './EntityContext';

type EntityMap = Record<string, Entity>;

export function EntityProvider({ children }: { children: ReactNode }): ReactElement {
  const [entityMap, setEntityMap] = useState<EntityMap>({});

  const setEntity = useCallback(
    (entity: Entity): void => {
      const key = `${entity.type}:${entity.entity_id}`;

      if (isEqual(entityMap[key], entity)) {
        return;
      }

      setEntityMap((prev) => {
        return { ...prev, [key]: entity };
      });
    },
    [entityMap],
  );

  const getEntity = useCallback(
    <T extends Entity>(entity_type: string, entity_id: string): T | undefined => {
      return entityMap[`${entity_type}:${entity_id}`] as T | undefined;
    },
    [entityMap],
  );

  const value: EntityContextType = useMemo(
    () => ({
      setEntity,
      getEntity,
    }),
    [setEntity, getEntity],
  );

  return <EntityContext.Provider value={value}>{children}</EntityContext.Provider>;
}
